import api from './api'

export const ORDER_STATUSES = [
  'draft',
  'pending_confirmation',
  'confirmed',
  'in_progress',
  'ready',
  'completed',
  'cancelled',
] as const

export type OrderStatus = (typeof ORDER_STATUSES)[number]

/** Un estado nuevo del backend nunca debe romper la UI desplegada. */
export function normalizeOrderStatus(value: unknown): OrderStatus {
  return ORDER_STATUSES.includes(value as OrderStatus) ? (value as OrderStatus) : 'pending_confirmation'
}

export type OrderSource = 'manual' | 'catalog' | 'checkout'

export interface OrderItem {
  id: string
  productId: string | null
  position: number
  quantity: number
  sku: string
  name: string
  slug: string | null
  description: string | null
  unitPriceCents: number
  lineTotalCents: number
  currency: string
  isFeatured: boolean
  imageUrl: string | null
  categoryId: string | null
  categoryName: string | null
  categorySlug: string | null
  createdAt: string
  updatedAt: string
}

export interface OrderTimelineEntry {
  status: OrderStatus
  label: string
  at: string
  source: string
}

export interface OrderResponse {
  id: string
  tenantId: string
  orderNumber: string
  idempotencyKey: string
  source: OrderSource | string
  status: OrderStatus
  currency: string
  subtotalCents: number
  totalCents: number
  itemCount: number
  customerName: string | null
  customerPhone: string | null
  customerEmail: string | null
  fulfillmentMethod: string | null
  fulfillmentAddress: string | null
  fulfillmentReference: string | null
  notes: string | null
  conversationId: string | null
  contactId: string | null
  channelInstanceId: string | null
  confirmedAt: string | null
  startedAt: string | null
  readyAt: string | null
  completedAt: string | null
  cancelledAt: string | null
  createdAt: string
  updatedAt: string
  items: OrderItem[]
  conversation?: {
    id: string
    waContact: string
    contactName: string | null
    status: string
    channelInstanceId: string | null
  } | null
  contact?: {
    id: string
    name: string | null
    waContact?: string | null
    phone?: string | null
    email?: string | null
  } | null
  channelInstance?: {
    id: string
    instanceName: string
    channelType: string
    status: string
  } | null
  history: OrderTimelineEntry[]
}

export interface OrdersPagination {
  page: number
  limit: number
  total: number
  totalPages: number
}

export interface OrdersResponse {
  orders: OrderResponse[]
  pagination: OrdersPagination
}

export interface OrdersFilters {
  status?: OrderStatus | 'all'
  statuses?: OrderStatus[]
  search?: string
  conversationId?: string
  contactId?: string
  channelInstanceId?: string
  from?: string
  to?: string
  page?: number
  limit?: number
}

export interface OrderCreateInput {
  source?: OrderSource
  status?: OrderStatus
  conversationId?: string | null
  contactId?: string | null
  channelInstanceId?: string | null
  customerName?: string | null
  customerPhone?: string | null
  customerEmail?: string | null
  fulfillmentMethod?: string | null
  fulfillmentAddress?: string | null
  fulfillmentReference?: string | null
  notes?: string | null
  idempotencyKey?: string | null
  items: Array<{ productId: string; quantity?: number }>
}

export interface OrderUpdateInput {
  contactId?: string | null
  customerName?: string | null
  customerPhone?: string | null
  customerEmail?: string | null
  fulfillmentMethod?: string | null
  fulfillmentAddress?: string | null
  fulfillmentReference?: string | null
  notes?: string | null
  items?: Array<{ productId: string; quantity?: number }>
}

export interface OrderStatusUpdateInput {
  status: OrderStatus
  note?: string | null
}

export interface OrderReceiptInfo {
  orderId: string
  filePath: string
  fileName: string
  downloadUrl: string
  mimeType: 'image/png' | 'application/pdf'
  generatedAt: string
}

function buildQuery(params: Record<string, string | number | readonly string[] | undefined>) {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue
    if (Array.isArray(value)) {
      if (value.length === 0) continue
      search.set(key, value.join(','))
      continue
    }
    search.set(key, String(value))
  }
  return search.toString()
}

function normalizeOrder(order: any): OrderResponse {
  return {
    ...order,
    status: normalizeOrderStatus(order.status),
    items: Array.isArray(order.items) ? order.items : [],
    history: Array.isArray(order.history)
      ? order.history.map((entry: any) => ({ ...entry, status: normalizeOrderStatus(entry.status) }))
      : [],
  }
}

export interface PendingOrderPreview {
  id: string
  orderNumber: string
  customerName: string | null
  totalCents: number
  currency: string
  itemCount: number
  fulfillmentMethod: string | null
  conversationId: string | null
  createdAt: string
}

/** Conteos reales por estado (todas las órdenes del negocio, no solo la página cargada). */
export interface OrdersSummary {
  pending: { count: number; totalCents: number; oldestCreatedAt: string | null }
  active: { count: number }
  completed: { count: number }
  completedToday: { count: number; totalCents: number }
  cancelled: { count: number }
  total: number
  pendingOrders: PendingOrderPreview[]
}

export const ordersApi = {
  async getSummary(): Promise<OrdersSummary> {
    const response = await api.get('/orders/summary')
    return response.data.data as OrdersSummary
  },

  async getOrders(filters: OrdersFilters = {}): Promise<OrdersResponse> {
    const query = buildQuery({
      status: filters.statuses?.length ? undefined : filters.status,
      statuses: filters.statuses,
      search: filters.search,
      conversationId: filters.conversationId,
      contactId: filters.contactId,
      channelInstanceId: filters.channelInstanceId,
      from: filters.from,
      to: filters.to,
      page: filters.page,
      limit: filters.limit,
    })

    const response = await api.get(`/orders${query ? `?${query}` : ''}`)
    return {
      orders: (response.data.data as any[]).map(normalizeOrder),
      pagination: response.data.pagination,
    }
  },

  async getOrder(id: string): Promise<OrderResponse> {
    const response = await api.get(`/orders/${id}`)
    return normalizeOrder(response.data.data)
  },

  async createOrder(payload: OrderCreateInput): Promise<OrderResponse> {
    const response = await api.post('/orders', payload)
    return normalizeOrder(response.data.data)
  },

  async updateOrder(id: string, payload: OrderUpdateInput): Promise<OrderResponse> {
    const response = await api.patch(`/orders/${id}`, payload)
    return normalizeOrder(response.data.data)
  },

  async updateOrderStatus(id: string, payload: OrderStatusUpdateInput): Promise<OrderResponse> {
    const response = await api.patch(`/orders/${id}/status`, payload)
    return normalizeOrder(response.data.data)
  },

  async getOrderReceiptInfo(id: string): Promise<OrderReceiptInfo> {
    const response = await api.get(`/orders/${id}/receipt`)
    return response.data.data
  },

  async getOrderReceiptImage(id: string): Promise<Blob> {
    const response = await api.get(`/orders/${id}/receipt/image`, {
      responseType: 'blob',
    })
    return response.data as Blob
  },

  async getOrderReceiptPdf(id: string): Promise<Blob> {
    const response = await api.get(`/orders/${id}/receipt/pdf`, {
      responseType: 'blob',
    })
    return response.data as Blob
  },
}

export default ordersApi
